export interface DeviceContext {
  deviceType: 'mobile' | 'tablet' | 'desktop';
  screenResolution: string;
  referrer: string;
  language: string;
  timeZone: string;
}

export interface PageViewEvent {
  path: string;
  title?: string;
  timestamp: string;
  deviceContext: DeviceContext;
}

export interface AnalyticsEvent {
  name: string;
  params?: Record<string, any>;
  timestamp: string;
  deviceContext: DeviceContext;
}

export interface IAnalyticsService {
  trackPageView(path: string, title?: string): void;
  trackEvent(name: string, params?: Record<string, any>): void;
  getHistory(): PageViewEvent[];
  getSummary(): Record<string, any>;
}

export function detectDeviceContext(): DeviceContext {
  const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
  let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';

  if (width < 600) {
    deviceType = 'mobile';
  } else if (width < 1024) {
    deviceType = 'tablet';
  }

  const rawReferrer = typeof document !== 'undefined' && document.referrer ? document.referrer : '';
  const referrer = rawReferrer ? rawReferrer.replace(/^https?:\/\//, '').split('/')[0] : 'direct';

  const screenResolution = typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : 'unknown';
  const language = typeof navigator !== 'undefined' ? navigator.language : 'en-US';
  const timeZone = typeof Intl !== 'undefined' && Intl.DateTimeFormat
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : 'unknown';

  return {
    deviceType,
    screenResolution,
    referrer,
    language,
    timeZone,
  };
}

/**
 * Privacy-First, 100% FREE Instrumented Analytics & Observability Service.
 * - Posts real HTTP telemetry directly to Cloud Firestore (`analytics_pageviews`) on project `geireann`.
 * - Anonymous Device Metrics (deviceType, resolution, referrer, timezone)
 * - Zero tracking cookies
 * - Fully GDPR & CCPA compliant
 */
export class AnalyticsService implements IAnalyticsService {
  private history: PageViewEvent[] = [];
  private lastTrackedPath: string | null = null;

  constructor() {
    if (typeof window !== 'undefined' && import.meta.env?.DEV) {
      (window as any).getAnalyticsSummary = () => this.getSummary();
    }
  }

  public trackPageView(path: string, title?: string): void {
    if (this.lastTrackedPath === path) {
      return; // Prevent duplicate consecutive logs
    }

    this.lastTrackedPath = path;
    const deviceContext = detectDeviceContext();
    const pageView: PageViewEvent = {
      path,
      title: title || (typeof document !== 'undefined' ? document.title : ''),
      timestamp: new Date().toISOString(),
      deviceContext,
    };

    this.history.push(pageView);

    // 1. Post real HTTP telemetry to Cloud Firestore
    this.postToFirestore(pageView);

    // 2. Send non-blocking beacon
    this.sendPrivacyBeacon(pageView);

    if (import.meta.env?.DEV) {
      console.log(`[Analytics Telemetry HTTP POST] 📊 Posted PageView to Cloud Firestore: ${path}`, pageView);
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('pageview', {
          detail: pageView,
          bubbles: true,
        })
      );
    }
  }

  public trackEvent(name: string, params?: Record<string, any>): void {
    const deviceContext = detectDeviceContext();
    const event: AnalyticsEvent = {
      name,
      params,
      timestamp: new Date().toISOString(),
      deviceContext,
    };

    if (import.meta.env?.DEV) {
      console.log(`[Analytics Telemetry] ⚡ Event: ${name}`, event);
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('analytics-event', {
          detail: event,
          bubbles: true,
        })
      );
    }
  }

  private async postToFirestore(pageView: PageViewEvent): Promise<void> {
    try {
      const firestoreUrl = 'https://firestore.googleapis.com/v1/projects/geireann/databases/(default)/documents/analytics_pageviews';
      const payload = {
        fields: {
          path: { stringValue: pageView.path },
          title: { stringValue: pageView.title || 'Untitled' },
          deviceType: { stringValue: pageView.deviceContext.deviceType },
          screenResolution: { stringValue: pageView.deviceContext.screenResolution },
          referrer: { stringValue: pageView.deviceContext.referrer },
          language: { stringValue: pageView.deviceContext.language },
          timeZone: { stringValue: pageView.deviceContext.timeZone },
          timestamp: { stringValue: pageView.timestamp },
        },
      };

      if (typeof fetch !== 'undefined') {
        await fetch(firestoreUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
      }
    } catch (err) {
      // Non-blocking catch to guarantee main thread performance
    }
  }

  private sendPrivacyBeacon(pageView: PageViewEvent): void {
    try {
      if (typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'test') {
        return; // Guard network call during unit tests
      }
      const beaconUrl = `https://geireann.goatcounter.com/count?p=${encodeURIComponent(pageView.path)}&t=${encodeURIComponent(pageView.title || '')}`;
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon(beaconUrl);
      }
    } catch (e) {}
  }

  public getHistory(): PageViewEvent[] {
    return [...this.history];
  }

  public getSummary(): Record<string, any> {
    const totalViews = this.history.length;
    const deviceBreakdown = this.history.reduce((acc, ev) => {
      const type = ev.deviceContext.deviceType;
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const pageBreakdown = this.history.reduce((acc, ev) => {
      acc[ev.path] = (acc[ev.path] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalViews,
      deviceBreakdown,
      pageBreakdown,
      recentHistory: this.history.slice(-10),
    };
  }
}

export const analyticsService = new AnalyticsService();
